import {
    AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
    EventEmitter, Input, OnChanges, Output, QueryList, ViewChildren, forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Código de un solo uso: una casilla por dígito.
 *
 * Cada casilla es un `<input>` real y no un `div` con foco, para que tres cosas
 * funcionen sin código nuestro: el teclado numérico del teléfono
 * (`inputmode="numeric"`), el autorrelleno del SMS (`autocomplete="one-time-code"`
 * en la primera) y el lector de pantalla.
 *
 * Decisiones, como en `dxc-time-field`:
 *
 * - **Avance automático.** Al teclear un dígito el foco pasa a la siguiente; con
 *   Retroceso en una casilla vacía vuelve a la anterior y la borra.
 * - **Pegar reparte.** Un código pegado —o el que el sistema autorrellena de golpe
 *   en la primera casilla— se reparte entre las casillas desde donde está el foco.
 * - **Solo dígitos.** Lo demás se descarta al teclear y al pegar.
 * - **Valor `string`**, parcial mientras se escribe. `(completed)` se emite una
 *   sola vez cuando están todas llenas.
 */
@Component({
    standalone: true,
    selector: 'dxc-otp-field',
    templateUrl: './dxc-otp-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DxcOtpFieldComponent), multi: true }],
})
export class DxcOtpFieldComponent implements ControlValueAccessor, AfterViewInit, OnChanges {

    @Input() length = 6;
    @Input() invalid = false;
    @Input() ariaLabel = 'Código de verificación';
    /** Enfoca la primera casilla al montarse. */
    @Input() autofocus = false;

    @Output() completed = new EventEmitter<string>();

    @ViewChildren('cellInput') inputs!: QueryList<ElementRef<HTMLInputElement>>;

    digits: string[] = [];
    disabled = false;

    #onChange: (value: string) => void = () => { };
    #onTouched: () => void = () => { };

    constructor(private cdr: ChangeDetectorRef) { this.#resize(); }

    get cells(): number[] { return Array.from({ length: this.length }, (_, i) => i); }

    ngOnChanges() { this.#resize(); }

    ngAfterViewInit() {
        if (this.autofocus) this.#focus(0);
    }

    // --- ControlValueAccessor ------------------------------------------------

    writeValue(value: string | null) {
        const clean = (value || '').replace(/\D/g, '').slice(0, this.length);
        this.digits = Array.from({ length: this.length }, (_, i) => clean[i] || '');
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string) => void) { this.#onChange = fn; }
    registerOnTouched(fn: () => void) { this.#onTouched = fn; }
    setDisabledState(disabled: boolean) { this.disabled = disabled; this.cdr.markForCheck(); }

    // --- Entrada -------------------------------------------------------------

    onInput(index: number, text: string) {
        const typed = (text || '').replace(/\D/g, '');
        if (!typed) {
            this.digits[index] = '';
            this.#syncInput(index);
            this.#emit();
            return;
        }
        // Más de un dígito de golpe: autorrelleno del SMS o un pegado sin evento.
        this.#fill(index, typed);
    }

    onPaste(index: number, event: ClipboardEvent) {
        const text = event.clipboardData?.getData('text') || '';
        const typed = text.replace(/\D/g, '');
        event.preventDefault();
        if (typed) this.#fill(index, typed);
    }

    onKeydown(index: number, event: KeyboardEvent) {
        switch (event.key) {
            case 'Backspace':
                if (!this.digits[index] && index > 0) {
                    event.preventDefault();
                    this.digits[index - 1] = '';
                    this.#syncInput(index - 1);
                    this.#focus(index - 1);
                    this.#emit();
                }
                break;
            case 'ArrowLeft': event.preventDefault(); this.#focus(index - 1); break;
            case 'ArrowRight': event.preventDefault(); this.#focus(index + 1); break;
            case 'Home': event.preventDefault(); this.#focus(0); break;
            case 'End': event.preventDefault(); this.#focus(this.length - 1); break;
        }
    }

    onBlur() { this.#onTouched(); }

    // --- Interno -------------------------------------------------------------

    #resize() {
        const next = Array.from({ length: this.length }, (_, i) => this.digits[i] || '');
        this.digits = next;
    }

    /** Reparte `typed` desde `start`; el foco queda en la siguiente casilla vacía. */
    #fill(start: number, typed: string) {
        let at = start;
        for (const ch of typed) {
            if (at >= this.length) break;
            this.digits[at] = ch;
            this.#syncInput(at);
            at++;
        }
        this.#focus(Math.min(at, this.length - 1));
        this.#emit();
    }

    /** El `[value]` de una casilla no se vuelve a escribir si el estado no cambia: se fuerza. */
    #syncInput(index: number) {
        const el = this.inputs?.get(index)?.nativeElement;
        if (el) el.value = this.digits[index] || '';
    }

    #focus(index: number) {
        const el = this.inputs?.get(Math.max(0, Math.min(this.length - 1, index)))?.nativeElement;
        el?.focus();
        el?.select();
    }

    #emit() {
        const value = this.digits.join('');
        this.#onChange(value);
        this.cdr.markForCheck();
        if (value.length === this.length && this.digits.every(Boolean)) this.completed.emit(value);
    }
}
